// ============================================================
//  ALTIMO Medical Center — EstacionEnfermeria.jsx  (v2)
//  Sub-módulo de Enfermería: Estación de piso/área
//
//  MODELO v2 — consumo acumulado + cobro consolidado:
//  Farmacia ──(transferencia)──▶ Estación
//  Estación ──(registrar dosis, NO cobra)──▶ Paciente   [N veces]
//  Estación ──(CONSOLIDAR: agrupa y cobra)──▶ Cuenta
//  Estación ──(sobrante, NO cobrado)──▶ Farmacia
//
//  La enfermera puede registrar cualquier número de dosis con
//  cantidades distintas. El cobro real solo ocurre al consolidar
//  (botón manual aquí, o automático al cerrar la cuenta del
//  paciente). El detalle de cada dosis individual (fecha/hora/
//  usuario/cantidad) queda siempre disponible para auditoría,
//  incluso después de facturado.
//
//  Se exporta PanelEstacionEnfermeria para integrarse como una
//  pestaña más dentro de Enfermeria.jsx.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

const C = {
  azul:   '#0f766e',
  azulOsc:'#134e4a',
  verde:  '#1D9E75',
  rojo:   '#dc3545',
  naranja:'#e67e22',
  morado: '#7c3aed',
  gris:   '#6c757d',
};

const S = {
  card:   { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', margin:'0 24px 14px' },
  titulo: { fontSize:'12px', fontWeight:'700', color: C.azulOsc, textTransform:'uppercase', letterSpacing:'.05em',
            marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f4f3',
            display:'flex', justifyContent:'space-between', alignItems:'center' },
  input:  { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda', borderRadius:'7px',
            background:'#fff', outline:'none', boxSizing:'border-box' },
  label:  { display:'block', fontSize:'11px', fontWeight:'600', color: C.gris, marginBottom:'4px',
            textTransform:'uppercase', letterSpacing:'.04em' },
  campo:  { marginBottom:'12px' },
  btnPrimario: { background: C.azul, color:'#fff', border:'none', borderRadius:'7px', padding:'8px 18px',
                 fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnSecundario: { background:'#fff', color:'#495057', border:'1px solid #c0ccda', borderRadius:'7px',
                   padding:'7px 14px', fontSize:'12px', cursor:'pointer' },
  pill: (color) => ({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: { verde:'#d4edda', amber:'#fff3cd', azul:'#cce5ff', rojo:'#f8d7da', gris:'#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde:'#155724', amber:'#856404', azul:'#004085', rojo:'#721c24', gris:'#383d41' }[color] || '#333',
  }),
};

const SUBTABS = [
  { id:'administrar', label:'💊 Administrar a paciente' },
  { id:'consolidar',  label:'🧾 Consolidar y facturar' },
  { id:'stock',       label:'📦 Stock de la estación' },
  { id:'kardex',      label:'📜 Movimientos' },
];

const fmtFechaHora = (s) => {
  if (!s) return '—';
  try { return new Date(String(s).replace(' ', 'T')).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' }); }
  catch (_) { return s; }
};

// ── Selector de estación (la enfermera elige su piso/área) ──
function SelectorEstacion({ estaciones, estacionId, onCambiar }) {
  return (
    <div style={{ margin:'0 24px 14px' }}>
      <label style={S.label}>Estación activa</label>
      <select style={{ ...S.input, maxWidth:'320px' }} value={estacionId || ''}
        onChange={e => onCambiar(e.target.value ? Number(e.target.value) : null)}>
        <option value="">Selecciona tu estación...</option>
        {estaciones.map(e => (
          <option key={e.id} value={e.id}>{e.nombre}{e.piso_area ? ` — ${e.piso_area}` : ''}</option>
        ))}
      </select>
    </div>
  );
}

// ── Panel: prescripciones ya transferidas, listas para administrar ──
function PanelAdministrar({ usuario, estacionId }) {
  const [lista,     setLista]     = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [modal,     setModal]     = useState(null); // prescripción a administrar
  const [detalle,   setDetalle]   = useState(null);  // prescripción para ver detalle de dosis

  const cargar = useCallback(async () => {
    if (!estacionId) { setLista([]); setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.prescripciones.enEstacion({ estacion_id: estacionId });
      setLista(r?.ok ? r.data : []);
    } catch(_) { setLista([]); }
    setCargando(false);
  }, [estacionId]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 15000);
    return () => clearInterval(t);
  }, [cargar]);

  if (!estacionId) {
    return <div style={S.card}><div style={{ fontSize:'13px', color: C.gris }}>Selecciona tu estación para ver lo disponible.</div></div>;
  }

  return (
    <div style={S.card}>
      <div style={S.titulo}>
        <span>💊 Disponibles para administrar ({lista.length})</span>
      </div>
      <div style={{ fontSize:'11px', color: C.gris, marginBottom:'12px' }}>
        Puedes registrar varias dosis para la misma prescripción a lo largo del turno.
        El cobro se genera al consolidar, no en cada dosis.
      </div>

      {cargando ? (
        <div style={{ fontSize:'12px', color: C.gris }}>Cargando...</div>
      ) : lista.length === 0 ? (
        <div style={{ fontSize:'12px', color: C.gris }}>No hay medicamentos en espera en esta estación.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {lista.map(p => (
            <div key={p.id} style={{ border:'1px solid #e3e9f0', borderRadius:'8px', padding:'12px 14px',
              display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc }}>{p.paciente} · Hab. {p.habitacion || '—'}</div>
                <div style={{ fontSize:'12px', color:'#374151', marginTop:'2px' }}>
                  {p.medicamento} — recetado {p.cantidad_recetada} {p.unidad}, transferido {p.cantidad_transferida} {p.unidad}
                </div>
                {p.es_controlado === 1 && <span style={{ ...S.pill('rojo'), marginTop:'4px' }}>Controlado</span>}
              </div>
              <div style={{ display:'flex', gap:'6px' }}>
                <button style={S.btnSecundario} onClick={() => setDetalle(p)}>Ver dosis</button>
                <button style={S.btnPrimario} onClick={() => setModal(p)}>+ Registrar dosis</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ModalAdministrar presc={modal} usuario={usuario} estacionId={estacionId}
          onCerrar={() => setModal(null)}
          onListo={() => { cargar(); }} />
      )}

      {detalle && (
        <ModalDetalleDosis presc={detalle} onCerrar={() => setDetalle(null)} />
      )}
    </div>
  );
}

// ── Modal: registrar UNA dosis (no cobra, puede repetirse) ──
function ModalAdministrar({ presc, usuario, estacionId, onCerrar, onListo }) {
  const [cantidad,   setCantidad]   = useState('');
  const [procesando, setProcesando] = useState(false);
  const [err,        setErr]        = useState('');
  const [exito,      setExito]      = useState(null); // { cantidad } de la última dosis registrada

  const confirmar = async () => {
    setErr(''); setExito(null);
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setErr('Ingresa una cantidad válida.'); return; }
    setProcesando(true);
    try {
      const r = await api.prescripciones.administrarEnEstacion(presc.id, {
        usuario_id: usuario.id, cantidad_administrada: cant,
      });
      if (r?.ok) {
        setExito({ cantidad: cant });
        setCantidad('');
        onListo(); // refresca la lista de fondo sin cerrar el modal
      } else {
        setErr(r?.error || 'No se pudo registrar la dosis.');
      }
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,30,30,.75)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'460px', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azul, padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>💊 Registrar dosis administrada</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            Esto NO genera cargo todavía — se factura al consolidar.
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc }}>{presc.paciente} · Hab. {presc.habitacion || '—'}</div>
            <div style={{ fontSize:'12px', color: C.gris, marginTop:'2px' }}>
              {presc.medicamento} · Recetado: {presc.cantidad_recetada} {presc.unidad}
            </div>
          </div>

          {exito && (
            <div style={{ fontSize:'12px', color:'#155724', background:'#d4edda', borderRadius:'8px', padding:'10px', marginBottom:'14px' }}>
              ✔ Dosis de {exito.cantidad} {presc.unidad} registrada. Puedes registrar otra si corresponde.
            </div>
          )}

          <label style={S.label}>Cantidad de esta dosis *</label>
          <input style={S.input} type="number" min="0.01" step="any" autoFocus
            value={cantidad} onChange={e => setCantidad(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmar(); }} />

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btnSecundario, flex:1, textAlign:'center' }} onClick={onCerrar}>
              {exito ? 'Listo, cerrar' : 'Cancelar'}
            </button>
            <button style={{ ...S.btnPrimario, flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Registrando...' : '✔ Registrar esta dosis'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: detalle de dosis individuales de una prescripción ──
// (fecha, hora, usuario, cantidad — facturadas o pendientes)
function ModalDetalleDosis({ presc, onCerrar }) {
  const [dosis,     setDosis]     = useState([]);
  const [cargando,  setCargando]  = useState(true);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const r = await api.estaciones.detalleConsumo({
          admision_id: presc.admision_id, inventario_id: presc.inventario_id,
        });
        setDosis(r?.ok ? r.data : []);
      } catch(_) { setDosis([]); }
      setCargando(false);
    })();
  }, [presc]);

  const totalAdministrado = dosis.reduce((acc, d) => acc + Number(d.cantidad), 0);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,30,30,.75)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'520px', maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azulOsc, padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>📋 Detalle de dosis — {presc.medicamento}</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>{presc.paciente}</div>
        </div>
        <div style={{ padding:'18px 20px', overflowY:'auto', flex:1 }}>
          {cargando ? (
            <div style={{ fontSize:'12px', color: C.gris }}>Cargando...</div>
          ) : dosis.length === 0 ? (
            <div style={{ fontSize:'12px', color: C.gris }}>Aún no se ha registrado ninguna dosis.</div>
          ) : (
            <>
              <div style={{ fontSize:'12px', fontWeight:700, color: C.azulOsc, marginBottom:'10px' }}>
                Total administrado: {totalAdministrado} {presc.unidad} en {dosis.length} aplicación(es)
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {dosis.map(d => (
                  <div key={d.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    borderBottom:'1px solid #f1f4f8', padding:'6px 4px', fontSize:'12px' }}>
                    <div>
                      <b>{d.cantidad} {d.unidad}</b>
                      <span style={{ color: C.gris }}> · {d.usuario}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <span style={{ color: C.gris, fontSize:'11px' }}>{fmtFechaHora(d.creado_en)}</span>
                      <span style={S.pill(d.cuenta_item_id ? 'verde' : 'amber')}>
                        {d.cuenta_item_id ? 'Facturado' : 'Pendiente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ padding:'14px 20px', borderTop:'1px solid #e3e9f0' }}>
          <button style={{ ...S.btnSecundario, width:'100%', textAlign:'center' }} onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Panel: consolidar consumos pendientes de una admisión ──
function PanelConsolidar({ usuario, estacionId }) {
  const [admisionId, setAdmisionId] = useState('');
  const [pendientes, setPendientes] = useState([]);
  const [buscando,   setBuscando]   = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [err,        setErr]        = useState('');
  const [resultado,  setResultado]  = useState(null);

  const buscarPendientes = async () => {
    setErr(''); setResultado(null);
    if (!admisionId) { setErr('Ingresa el número de admisión del paciente.'); return; }
    setBuscando(true);
    try {
      const r = await api.estaciones.pendientesConsolidar(Number(admisionId));
      setPendientes(r?.ok ? r.data : []);
      if (r?.ok && r.data.length === 0) setErr('No hay consumos pendientes de facturar para esta admisión.');
    } catch(e) { setErr(e.message); }
    setBuscando(false);
  };

  const confirmarConsolidar = async () => {
    setProcesando(true);
    setErr('');
    try {
      const r = await api.estaciones.consolidar({ admision_id: Number(admisionId), usuario_id: usuario.id });
      if (r?.ok) {
        setResultado(r.data);
        setPendientes([]);
      } else {
        setErr(r?.error || 'No se pudo consolidar.');
      }
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  const totalEstimado = pendientes.reduce((acc, p) => acc + Number(p.cantidad_pendiente) * Number(p.precio_unitario || 0), 0);

  return (
    <div style={S.card}>
      <div style={S.titulo}><span>🧾 Consolidar y facturar consumos de estación</span></div>
      <div style={{ fontSize:'11px', color: C.gris, marginBottom:'14px' }}>
        Agrupa todas las dosis pendientes de una admisión por producto y genera un cargo único por cada uno.
        Esto también ocurre automáticamente al cerrar la cuenta del paciente, así que este botón es opcional
        para cortes intermedios (ej. fin de turno).
      </div>

      <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', marginBottom:'16px' }}>
        <div style={{ flex:1 }}>
          <label style={S.label}>Número de admisión del paciente</label>
          <input style={S.input} type="number" value={admisionId}
            onChange={e => setAdmisionId(e.target.value)} placeholder="Ej: 1042" />
        </div>
        <button style={S.btnSecundario} disabled={buscando} onClick={buscarPendientes}>
          {buscando ? 'Buscando...' : 'Ver pendientes'}
        </button>
      </div>

      {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginBottom:'12px' }}>⚠ {err}</div>}

      {resultado && (
        <div style={{ fontSize:'12px', color:'#155724', background:'#d4edda', borderRadius:'8px', padding:'12px', marginBottom:'16px' }}>
          ✔ Se generaron {resultado.total} cargo(s) consolidado(s) correctamente.
        </div>
      )}

      {pendientes.length > 0 && (
        <>
          <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse', marginBottom:'14px' }}>
            <thead>
              <tr style={{ textAlign:'left', color: C.gris, borderBottom:'1px solid #e3e9f0' }}>
                <th style={{ padding:'6px 4px' }}>Producto</th>
                <th style={{ padding:'6px 4px' }}>Dosis aplicadas</th>
                <th style={{ padding:'6px 4px' }}>Cantidad total</th>
                <th style={{ padding:'6px 4px' }}>Periodo</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((p, i) => (
                <tr key={i} style={{ borderBottom:'1px solid #f1f4f8' }}>
                  <td style={{ padding:'8px 4px', fontWeight:600 }}>{p.producto}</td>
                  <td style={{ padding:'8px 4px' }}>{p.cantidad_movimientos}</td>
                  <td style={{ padding:'8px 4px', fontWeight:700, color: C.azulOsc }}>{p.cantidad_pendiente} {p.unidad}</td>
                  <td style={{ padding:'8px 4px', color: C.gris, fontSize:'11px' }}>
                    {fmtFechaHora(p.primera_dosis)} — {fmtFechaHora(p.ultima_dosis)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc, marginBottom:'14px' }}>
            Total estimado a facturar: ${totalEstimado.toFixed(2)}
          </div>
          <button style={{ ...S.btnPrimario, opacity: procesando ? .6 : 1 }} disabled={procesando} onClick={confirmarConsolidar}>
            {procesando ? 'Procesando...' : '✔ Confirmar consolidación y facturar'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Panel: stock actual de la estación + devolver sobrante ──
function PanelStock({ usuario, estacionId }) {
  const [stock,     setStock]     = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [modal,     setModal]     = useState(null); // producto a devolver

  const cargar = useCallback(async () => {
    if (!estacionId) { setStock([]); setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.estaciones.stock(estacionId);
      setStock(r?.ok ? r.data : []);
    } catch(_) { setStock([]); }
    setCargando(false);
  }, [estacionId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!estacionId) {
    return <div style={S.card}><div style={{ fontSize:'13px', color: C.gris }}>Selecciona tu estación para ver el stock.</div></div>;
  }

  return (
    <div style={S.card}>
      <div style={S.titulo}><span>📦 Stock actual de la estación</span></div>

      {cargando ? (
        <div style={{ fontSize:'12px', color: C.gris }}>Cargando...</div>
      ) : stock.length === 0 ? (
        <div style={{ fontSize:'12px', color: C.gris }}>Esta estación no tiene stock registrado.</div>
      ) : (
        <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ textAlign:'left', color: C.gris, borderBottom:'1px solid #e3e9f0' }}>
              <th style={{ padding:'6px 4px' }}>Producto</th>
              <th style={{ padding:'6px 4px' }}>Cantidad</th>
              <th style={{ padding:'6px 4px' }}></th>
            </tr>
          </thead>
          <tbody>
            {stock.map(s => (
              <tr key={s.id} style={{ borderBottom:'1px solid #f1f4f8' }}>
                <td style={{ padding:'8px 4px', fontWeight:600 }}>
                  {s.nombre} {s.es_controlado === 1 && <span style={S.pill('rojo')}>Controlado</span>}
                </td>
                <td style={{ padding:'8px 4px' }}>{s.cantidad_actual} {s.unidad}</td>
                <td style={{ padding:'8px 4px', textAlign:'right' }}>
                  {Number(s.cantidad_actual) > 0 && (
                    <button style={S.btnSecundario} onClick={() => setModal(s)}>↩ Devolver sobrante</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <ModalDevolverSobrante producto={modal} usuario={usuario} estacionId={estacionId}
          onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); cargar(); }} />
      )}
    </div>
  );
}

// ── Modal: devolver sobrante a Farmacia (nunca se cobró) ──
function ModalDevolverSobrante({ producto, usuario, estacionId, onCerrar, onListo }) {
  const [cantidad,   setCantidad]   = useState(String(producto.cantidad_actual));
  const [motivo,     setMotivo]     = useState('');
  const [procesando, setProcesando] = useState(false);
  const [err,        setErr]        = useState('');

  const confirmar = async () => {
    setErr('');
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setErr('Ingresa una cantidad válida.'); return; }
    if (cant > Number(producto.cantidad_actual)) { setErr(`Máximo disponible: ${producto.cantidad_actual}.`); return; }
    setProcesando(true);
    try {
      const r = await api.estaciones.devolverSobrante(estacionId, {
        inventario_id: producto.inventario_id, cantidad: cant,
        usuario_id: usuario.id, motivo: motivo.trim() || undefined,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo registrar la devolución.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,30,30,.75)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'460px', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azul, padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>↩ Devolver sobrante a Farmacia</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            Este producto no se cobró a ningún paciente, así que solo se reintegra al stock de Farmacia.
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc }}>{producto.nombre}</div>
            <div style={{ fontSize:'11px', color: C.gris, marginTop:'2px' }}>
              Disponible en estación: {producto.cantidad_actual} {producto.unidad}
            </div>
          </div>

          <label style={S.label}>Cantidad a devolver *</label>
          <input style={S.input} type="number" min="0.01" max={producto.cantidad_actual} step="any"
            value={cantidad} onChange={e => setCantidad(e.target.value)} />

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Motivo (opcional)</label>
            <input style={S.input} placeholder="Ej: caja abierta sin usar completa..."
              value={motivo} onChange={e => setMotivo(e.target.value)} />
          </div>

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btnSecundario, flex:1, textAlign:'center' }} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btnPrimario, flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Enviando...' : '✔ Confirmar devolución'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Panel: kardex / movimientos de la estación ──
function PanelKardex({ estacionId }) {
  const [movs,      setMovs]      = useState([]);
  const [cargando,  setCargando]  = useState(true);

  const cargar = useCallback(async () => {
    if (!estacionId) { setMovs([]); setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.estaciones.movimientos(estacionId);
      setMovs(r?.ok ? r.data : []);
    } catch(_) { setMovs([]); }
    setCargando(false);
  }, [estacionId]);

  useEffect(() => { cargar(); }, [cargar]);

  const ETIQ_MOV = {
    Transferencia_Entrada:           { color:'azul',  label:'⬇ Recibido de Farmacia' },
    Administracion_Paciente:         { color:'verde', label:'💊 Administrado' },
    Transferencia_Salida_Devolucion: { color:'gris',  label:'⬆ Devuelto a Farmacia' },
  };

  if (!estacionId) {
    return <div style={S.card}><div style={{ fontSize:'13px', color: C.gris }}>Selecciona tu estación para ver el historial.</div></div>;
  }

  return (
    <div style={S.card}>
      <div style={S.titulo}><span>📜 Últimos movimientos</span></div>

      {cargando ? (
        <div style={{ fontSize:'12px', color: C.gris }}>Cargando...</div>
      ) : movs.length === 0 ? (
        <div style={{ fontSize:'12px', color: C.gris }}>Sin movimientos registrados.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'6px', maxHeight:'520px', overflowY:'auto' }}>
          {movs.map(m => {
            const etiq = ETIQ_MOV[m.tipo_mov] || { color:'gris', label: m.tipo_mov };
            return (
              <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                borderBottom:'1px solid #f1f4f8', padding:'6px 4px', fontSize:'12px' }}>
                <div>
                  <span style={S.pill(etiq.color)}>{etiq.label}</span>{' '}
                  <b>{m.producto}</b> — {m.cantidad} {m.unidad}
                  {m.paciente && <span style={{ color: C.gris }}> · {m.paciente}</span>}
                  {m.tipo_mov === 'Administracion_Paciente' && (
                    <span style={{ ...S.pill(m.cuenta_item_id ? 'verde' : 'amber'), marginLeft:'6px' }}>
                      {m.cuenta_item_id ? 'Facturado' : 'Pendiente'}
                    </span>
                  )}
                </div>
                <div style={{ color: C.gris, fontSize:'11px' }}>{fmtFechaHora(m.creado_en)} · {m.usuario}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Export principal: pestaña "Estación" dentro de Enfermeria.jsx ──
export function PanelEstacionEnfermeria({ usuario }) {
  const [estaciones, setEstaciones] = useState([]);
  const [estacionId, setEstacionId] = useState(null);
  const [subtab,     setSubtab]     = useState('administrar');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.estaciones.listar();
        const lista = r?.ok ? r.data : [];
        setEstaciones(lista);
        // Si el usuario solo tiene una estación disponible, se preselecciona.
        if (lista.length === 1) setEstacionId(lista[0].id);
      } catch(_) { setEstaciones([]); }
    })();
  }, []);

  return (
    <div>
      <SelectorEstacion estaciones={estaciones} estacionId={estacionId} onCambiar={setEstacionId} />

      <div style={{ display:'flex', gap:'2px', margin:'0 24px 14px', flexWrap:'wrap' }}>
        {SUBTABS.map(t => (
          <button key={t.id} onClick={() => setSubtab(t.id)}
            style={{ padding:'8px 16px', border:'1px solid #dde3ea', cursor:'pointer', fontSize:'12px', fontWeight:'700',
              borderRadius:'7px', fontFamily:'inherit',
              background: subtab === t.id ? C.azul : '#fff',
              color:      subtab === t.id ? '#fff' : C.azulOsc }}>
            {t.label}
          </button>
        ))}
      </div>

      {subtab === 'administrar' && <PanelAdministrar usuario={usuario} estacionId={estacionId} />}
      {subtab === 'consolidar'  && <PanelConsolidar  usuario={usuario} estacionId={estacionId} />}
      {subtab === 'stock'       && <PanelStock       usuario={usuario} estacionId={estacionId} />}
      {subtab === 'kardex'      && <PanelKardex      estacionId={estacionId} />}
    </div>
  );
}

export default PanelEstacionEnfermeria;
